import React, { useState } from 'react';

/**
 * Progress overlay shown while parsing multiple cennik files.
 */
const MultiCennikProgress = ({ progress }) => {
  if (!progress) return null;

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
      alignItems: 'center', justifyContent: 'center', zIndex: 1000
    }}>
      <div style={{
        backgroundColor: 'var(--card-bg)', borderRadius: '8px', padding: '2rem', textAlign: 'center'
      }}>
        <div style={{ fontSize: '48px', marginBottom: '1rem' }}>{'📚'}</div>
        <h5 style={{ margin: '0 0 0.5rem 0' }}>Wczytywanie cennik{'ó'}w...</h5>
        <div style={{ color: 'var(--text-muted)', marginBottom: '1rem' }}>
          Plik {progress.current} z {progress.total}: {progress.currentFile}
        </div>
        <div style={{ height: '8px', backgroundColor: 'var(--border)', borderRadius: '4px', width: '300px', overflow: 'hidden' }}>
          <div style={{
            height: '100%',
            width: `${(progress.current / progress.total) * 100}%`,
            backgroundColor: 'var(--success)',
            transition: 'width 0.3s ease'
          }} />
        </div>
      </div>
    </div>
  );
};

/**
 * Modal showing preview of multiple cennik files before saving.
 */
const MultiCennikImportModal = ({
  multiCennikPreview, cennikOptions, setCennikOptions,
  multiCennikFilter, setMultiCennikFilter,
  multiCennikSearch, setMultiCennikSearch,
  isSavingMultiCennik, getFilteredMultiCennikProducts,
  onSave, onClose
}) => {
  // Lista plikow potrafi urosnac do kilkunastu rzedow przy imporcie wielu cennikow
  // i wypchnac tabele produktow poza modal - dlatego domyslnie jest zwinieta.
  const [filesExpanded, setFilesExpanded] = useState(false);

  if (!multiCennikPreview) return null;

  const filtered = getFilteredMultiCennikProducts();
  const filesCount = multiCennikPreview.files?.length || 0;

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex',
      alignItems: 'center', justifyContent: 'center', zIndex: 1000
    }}>
      <div style={{
        backgroundColor: 'var(--card-bg)', borderRadius: '8px', width: '95%', maxWidth: '1400px',
        maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden'
      }}>
        {/* Header */}
        <div style={{
          padding: '1rem', borderBottom: '1px solid var(--border)', flexShrink: 0,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          background: 'linear-gradient(135deg, var(--success), var(--success))'
        }}>
          <div style={{ color: '#fff' }}>
            <h5 style={{ margin: 0, fontSize: '16px' }}>{'📚'} Import wielu cennik{'ó'}w XML</h5>
            <div style={{ fontSize: '12px', opacity: 0.9 }}>
              {multiCennikPreview.stats?.files_count} plik{'ó'}w | {multiCennikPreview.stats?.total || 0} produkt{'ó'}w
            </div>
          </div>
          <button onClick={onClose}
            style={{ background: 'var(--bg-secondary)',  color: 'var(--text)' ,
              fontSize: '20px', cursor: 'pointer', padding: '4px 10px' }}>{'×'}</button>
        </div>

        {/* File badges & Stats */}
        <div style={{ padding: '1rem', borderBottom: '1px solid var(--border)', backgroundColor: 'var(--bg)', flexShrink: 0 }}>
          {/* File badges - wysokosc ograniczona, zeby nie zaslanialy listy produktow */}
          <div style={{ marginBottom: '1rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.35rem' }}>
              <span style={{ fontSize: '11px', fontWeight: '600', color: 'var(--text-secondary)' }}>
                {'📄'} Wczytane pliki ({filesCount})
                {(() => {
                  const failed = multiCennikPreview.files?.filter(f => !f.success).length || 0;
                  return failed > 0
                    ? <span style={{ color: 'var(--danger)', marginLeft: '0.4rem' }}>{'—'} b{'łę'}d{'ó'}w: {failed}</span>
                    : null;
                })()}
              </span>
              <button
                onClick={() => setFilesExpanded(v => !v)}
                style={{
                  padding: '2px 8px', fontSize: '10px', fontWeight: '500', cursor: 'pointer',
                  border: '1px solid var(--border)', borderRadius: '4px',
                  backgroundColor: 'var(--card-bg)', color: 'var(--text-secondary)'
                }}
                title={filesExpanded ? 'Zwiń listę plików' : 'Rozwiń listę plików'}
              >
                {filesExpanded ? '▲ Zwiń' : '▼ Pokaż'}
              </button>
            </div>
            <div style={{
              display: 'flex', gap: '0.5rem', flexWrap: 'wrap',
              maxHeight: filesExpanded ? '140px' : '48px',
              overflowY: 'auto', paddingRight: '2px'
            }}>
              {multiCennikPreview.files?.map((f, idx) => (
                <div key={idx} style={{
                  padding: '0.25rem 0.5rem', borderRadius: '4px', fontSize: '10px',
                  backgroundColor: f.success ? 'var(--success-light)' : 'var(--danger-light)',
                  color: f.success ? 'var(--success)' : 'var(--danger)',
                  whiteSpace: 'nowrap', height: 'fit-content'
                }}>
                  {f.success ? '✅' : '❌'} {f.file} {f.success && `(${f.count})`}
                </div>
              ))}
            </div>
          </div>

          <div style={{ display: 'flex', gap: '2rem', flexWrap: 'wrap', alignItems: 'center', marginBottom: '1rem' }}>
            <div style={{ display: 'flex', gap: '1rem' }}>
              <div style={{ textAlign: 'center', padding: '0.5rem 1rem', backgroundColor: 'var(--success-light)', borderRadius: '4px', cursor: 'pointer', border: multiCennikFilter === 'mapped' ? '2px solid var(--success)' : 'none' }}
                onClick={() => setMultiCennikFilter(multiCennikFilter === 'mapped' ? 'all' : 'mapped')}>
                <div style={{ fontSize: '20px', fontWeight: '700', color: 'var(--success)' }}>{multiCennikPreview.stats?.mapped || 0}</div>
                <div style={{ fontSize: '10px', color: 'var(--success)' }}>{'✅'} Zmapowane</div>
              </div>
              <div style={{ textAlign: 'center', padding: '0.5rem 1rem', backgroundColor: 'var(--warning-light)', borderRadius: '4px', cursor: 'pointer', border: multiCennikFilter === 'new' ? '2px solid var(--warning)' : 'none' }}
                onClick={() => setMultiCennikFilter(multiCennikFilter === 'new' ? 'all' : 'new')}>
                <div style={{ fontSize: '20px', fontWeight: '700', color: 'var(--warning)' }}>{multiCennikPreview.stats?.new || 0}</div>
                <div style={{ fontSize: '10px', color: 'var(--warning)' }}>{'➕'} Nowe</div>
              </div>
              <div style={{ textAlign: 'center', padding: '0.5rem 1rem', backgroundColor: 'var(--info-light)', borderRadius: '4px', cursor: 'pointer', border: multiCennikFilter === 'name_changes' ? '2px solid var(--info)' : 'none' }}
                onClick={() => setMultiCennikFilter(multiCennikFilter === 'name_changes' ? 'all' : 'name_changes')}>
                <div style={{ fontSize: '20px', fontWeight: '700', color: 'var(--info)' }}>{multiCennikPreview.stats?.name_changes || 0}</div>
                <div style={{ fontSize: '10px', color: 'var(--info)' }}>{'📝'} Zmiana nazwy</div>
              </div>
            </div>

            <input
              type="text"
              placeholder={'🔍 Szukaj produktu lub pliku...'}
              value={multiCennikSearch}
              onChange={e => setMultiCennikSearch(e.target.value)}
              style={{
                padding: '6px 10px', fontSize: '12px', borderRadius: '6px',
                border: '1px solid var(--border)', minWidth: '250px',
                backgroundColor: 'var(--card-bg)', color: 'var(--text)'
              }}
            />
          </div>

          <div style={{ display: 'flex', gap: '1.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '12px', cursor: 'pointer', padding: '0.5rem', backgroundColor: cennikOptions.update_prices ? 'var(--success-light)' : 'var(--bg)', borderRadius: '4px', border: '1px solid var(--border)' }}>
              <input type="checkbox" checked={cennikOptions.update_prices}
                onChange={e => setCennikOptions(prev => ({ ...prev, update_prices: e.target.checked }))} />
              {'💰'} Aktualizuj ceny sprzeda{'ż'}y
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '12px', cursor: 'pointer', padding: '0.5rem', backgroundColor: cennikOptions.update_names ? 'var(--success-light)' : 'var(--bg)', borderRadius: '4px', border: '1px solid var(--border)' }}>
              <input type="checkbox" checked={cennikOptions.update_names}
                onChange={e => setCennikOptions(prev => ({ ...prev, update_names: e.target.checked }))} />
              {'📝'} Aktualizuj nazwy produkt{'ó'}w
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '12px', cursor: 'pointer', padding: '0.5rem', backgroundColor: cennikOptions.create_new ? 'var(--success-light)' : 'var(--bg)', borderRadius: '4px', border: '1px solid var(--border)' }}>
              <input type="checkbox" checked={cennikOptions.create_new}
                onChange={e => setCennikOptions(prev => ({ ...prev, create_new: e.target.checked }))} />
              {'➕'} Tw{'ó'}rz nowe produkty
            </label>
          </div>
        </div>

        {/* Product list - minHeight:0 jest konieczne, inaczej flex nie pozwoli
            tabeli zwezic sie ponizej wysokosci tresci i scroll nie zadziala */}
        <div style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: '0.5rem' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '11px' }}>
            <thead style={{ position: 'sticky', top: 0, backgroundColor: 'var(--bg)', zIndex: 10 }}>
              <tr>
                <th style={{ padding: '0.5rem', textAlign: 'left', borderBottom: '2px solid var(--border)', width: '60px' }}>Status</th>
                <th style={{ padding: '0.5rem', textAlign: 'left', borderBottom: '2px solid var(--border)', width: '150px' }}>Plik</th>
                <th style={{ padding: '0.5rem', textAlign: 'left', borderBottom: '2px solid var(--border)', width: '120px' }}>Kod</th>
                <th style={{ padding: '0.5rem', textAlign: 'left', borderBottom: '2px solid var(--border)', width: '140px' }}>EAN</th>
                <th style={{ padding: '0.5rem', textAlign: 'left', borderBottom: '2px solid var(--border)' }}>Nazwa z cennika</th>
                <th style={{ padding: '0.5rem', textAlign: 'left', borderBottom: '2px solid var(--border)' }}>Nazwa w systemie</th>
                <th style={{ padding: '0.5rem', textAlign: 'right', borderBottom: '2px solid var(--border)', width: '100px' }}>Cena cennika</th>
                <th style={{ padding: '0.5rem', textAlign: 'right', borderBottom: '2px solid var(--border)', width: '100px' }}>Obecna cena</th>
              </tr>
            </thead>
            <tbody>
              {filtered.slice(0, 300).map((p, idx) => {
                const priceDiff = p.is_mapped && p.existing_price !== p.cena_brutto;
                const nameDiff = p.is_mapped && p.name_differs;

                return (
                  <tr key={idx} style={{
                    backgroundColor: p.is_mapped
                      ? (nameDiff ? 'var(--info-light)' : (idx % 2 === 0 ? 'var(--card-bg)' : 'var(--bg)'))
                      : 'var(--warning-light)'
                  }}>
                    <td style={{ padding: '0.4rem 0.5rem', borderBottom: '1px solid var(--border)' }}>
                      {p.is_mapped ? (nameDiff ? '📝' : '✅') : '➕'}
                    </td>
                    <td style={{ padding: '0.4rem 0.5rem', borderBottom: '1px solid var(--border)', fontSize: '9px', color: 'var(--text-muted)' }}>
                      {p.source_file}
                    </td>
                    <td style={{ padding: '0.4rem 0.5rem', borderBottom: '1px solid var(--border)', fontFamily: 'monospace', fontSize: '10px' }}>
                      {p.kod || '-'}
                    </td>
                    <td style={{ padding: '0.4rem 0.5rem', borderBottom: '1px solid var(--border)', fontFamily: 'monospace', fontSize: '10px' }}>
                      {p.ean || '-'}
                    </td>
                    <td style={{
                      padding: '0.4rem 0.5rem', borderBottom: '1px solid var(--border)',
                      maxWidth: '250px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      fontWeight: nameDiff ? '600' : 'normal'
                    }}>
                      {p.nazwa}
                    </td>
                    <td style={{
                      padding: '0.4rem 0.5rem', borderBottom: '1px solid var(--border)',
                      maxWidth: '250px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      color: p.is_mapped ? (nameDiff ? 'var(--danger)' : 'var(--success)') : 'var(--text-muted)',
                      fontStyle: nameDiff ? 'italic' : 'normal'
                    }}>
                      {p.existing_name || (p.is_mapped ? '-' : '(nowy produkt)')}
                    </td>
                    <td style={{
                      padding: '0.4rem 0.5rem', borderBottom: '1px solid var(--border)', textAlign: 'right',
                      fontWeight: '600', color: priceDiff ? 'var(--info)' : 'var(--text)'
                    }}>
                      {(p.cena_brutto || 0).toFixed(2)} z{'ł'}
                    </td>
                    <td style={{
                      padding: '0.4rem 0.5rem', borderBottom: '1px solid var(--border)', textAlign: 'right',
                      color: priceDiff ? 'var(--danger)' : 'var(--text-muted)',
                      textDecoration: priceDiff ? 'line-through' : 'none'
                    }}>
                      {p.existing_price ? `${p.existing_price.toFixed(2)} zł` : '-'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {filtered.length > 300 && (
            <div style={{ padding: '1rem', textAlign: 'center', color: 'var(--text-muted)', fontSize: '11px' }}>
              ... i {filtered.length - 300} wi{'ę'}cej produkt{'ó'}w
            </div>
          )}
          {filtered.length === 0 && (
            <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)' }}>
              Brak produkt{'ó'}w spe{'ł'}niaj{'ą'}cych kryteria filtrowania
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: '1rem', borderTop: '1px solid var(--border)', flexShrink: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: 'var(--bg)' }}>
          <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
            {multiCennikFilter !== 'all' && (
              <span>Filtr: {multiCennikFilter === 'mapped' ? 'zmapowane' : multiCennikFilter === 'new' ? 'nowe' : 'zmiany nazw'} | </span>
            )}
            Wy{'ś'}wietlono: {Math.min(filtered.length, 300)} z {filtered.length} produkt{'ó'}w
          </div>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button onClick={onClose}
              style={{ padding: '6px 12px', fontSize: '12px', border: '1px solid var(--border)',
                backgroundColor: 'var(--card-bg)', color: 'var(--text-secondary)', cursor: 'pointer' }}>
              Anuluj
            </button>
            <button onClick={onSave}
              disabled={isSavingMultiCennik}
              style={{
                padding: '6px 12px', fontSize: '12px',
                backgroundColor: isSavingMultiCennik ? 'var(--border)' : 'var(--success)',
                color: '#fff' , cursor: 'pointer', fontWeight: '600'
              }}>
              {isSavingMultiCennik ? '⏳ Zapisywanie...' : `✅ Aktualizuj cennik (${multiCennikPreview.stats?.total || 0} produktów)`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export { MultiCennikProgress };
export default MultiCennikImportModal;
